import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommandStore } from '../../store/commandStore';
import { useThemeStore } from '../../store/themeStore';
import { defaultCommands, processCommand, autocompleteCommand } from '../../services/commandService';
import { 
  typewriterEffect, 
  downloadCV, 
  applyTheme, 
  changeLanguage, 
  openGithub, 
  openLinkedin, 
  openTwitter,
  getDynamicDivider,
  getFullWidthDivider,
  getRealIpAddress
} from '../../utils/terminalUtils';
import {
  TerminalContainer,
  TerminalBody,
  TerminalInput,
  OutputLine,
  PromptSpan,
  InputField,
  SuccessLine,
  ErrorLine,
  InfoLine,
  DividerLine,
  TerminalHeader,
  WindowControls,
  WindowDot,
  HeaderTitle,
  HeaderActions,
  HeaderActionIcon,
  StatusBar,
  StatusItem,
  StatusDivider
} from './styles';

interface TerminalProps {
  onSwitchToClassic: () => void;
  onSwitchToServices: () => void;
  onSwitchToHome: () => void;
  onExitTerminal?: () => void;
  theme?: string;
  output?: JSX.Element[];
  setOutput?: React.Dispatch<React.SetStateAction<JSX.Element[]>>;
  ipAddress?: string;
  setIpAddress?: React.Dispatch<React.SetStateAction<string>>;
  isInitialized?: boolean;
  setIsInitialized?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Terminal: React.FC<TerminalProps> = ({
  onSwitchToClassic,
  onSwitchToServices,
  onSwitchToHome,
  onExitTerminal,
  theme: themeFromProps = 'ubuntu',
  output: externalOutput,
  setOutput: setExternalOutput,
  ipAddress: externalIpAddress,
  setIpAddress: setExternalIpAddress,
  isInitialized: externalInitialized,
  setIsInitialized: setExternalInitialized
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useThemeStore();
  const {
    commands,
    history,
    currentInput,
    setCurrentInput,
    historyIndex,
    addToHistory,
    moveHistoryUp,
    moveHistoryDown,
    setCommands
  } = useCommandStore();
  
  // Harici prop yoksa veya boşsa yerel state'i kullan, varsa harici state'i kullan
  const [output, setOutput] = useState<JSX.Element[]>(externalOutput || []);
  const [ipAddress, setIpAddress] = useState<string>(externalIpAddress || '');
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const terminalBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<JSX.Element[]>([]);
  const initializedRef = useRef<boolean>(externalInitialized || false);
  
  // Harici state'leri local state ile senkronize et
  useEffect(() => {
    if (setExternalOutput) {
      setExternalOutput(output);
    }
  }, [output, setExternalOutput]);
  
  useEffect(() => {
    if (setExternalIpAddress) {
      setExternalIpAddress(ipAddress);
    }
  }, [ipAddress, setExternalIpAddress]);
  
  useEffect(() => {
    if (setExternalInitialized) {
      setExternalInitialized(initializedRef.current);
    }
  }, [initializedRef.current, setExternalInitialized]);
  
  // Session timer
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionSeconds(s => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // IP adresini al
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const ip = await getRealIpAddress();
        setIpAddress(ip);
        
        // IP adresi alındıktan sonra, eğer terminal yeni başlatılıyorsa, karşılama mesajını göster
        if (!initializedRef.current || (output.length === 0 && !externalOutput?.length)) {
          updateWelcomeMessage();
          initializedRef.current = true;
        }
      } catch (error) {
        console.error('IP adresi alınırken hata oluştu:', error);
      }
    };
    
    fetchIpAddress();
  }, []);
  
  // Çıktı değişikliklerini ref'e kaydet
  useEffect(() => {
    outputRef.current = output;
  }, [output]);
  
  // Komut fonksiyonlarını memoize et
  const commandFunctions = useMemo(() => ({
    showHelp: () => {
      const helpItems = commands.filter(cmd => cmd.isVisible).map(cmd => ({
        name: cmd.name,
        description: t(cmd.descriptionKey)
      }));
      
      // Kategorilere göre komutları ayır
      const systemCommands = helpItems.filter(cmd => 
        commands.find(c => c.name === cmd.name)?.category === 'system'
      );
      
      const infoCommands = helpItems.filter(cmd => 
        commands.find(c => c.name === cmd.name)?.category === 'info'
      );
      
      const navigationCommands = helpItems.filter(cmd => 
        commands.find(c => c.name === cmd.name)?.category === 'navigation'
      );
      
      const socialCommands = helpItems.filter(cmd => 
        commands.find(c => c.name === cmd.name)?.category === 'social'
      );
      
      const miscCommands = helpItems.filter(cmd => 
        commands.find(c => c.name === cmd.name)?.category === 'misc'
      );
      
      const result: JSX.Element[] = [];
      
      result.push(<OutputLine key="help-title">{t('help.title')}</OutputLine>);
      
      // Sistem komutları
      if (systemCommands.length > 0) {
        result.push(<OutputLine key="help-system-title">{t('help.systemCommands')}</OutputLine>);
        systemCommands.forEach(cmd => {
          result.push(
            <InfoLine key={`help-${cmd.name}`}>
              <b>{cmd.name}</b> {cmd.description}
            </InfoLine>
          );
        });
        result.push(<OutputLine key="help-system-space">&nbsp;</OutputLine>);
      }
      
      // Bilgi komutları
      if (infoCommands.length > 0) {
        result.push(<OutputLine key="help-info-title">{t('help.infoCommands')}</OutputLine>);
        infoCommands.forEach(cmd => {
          result.push(
            <InfoLine key={`help-${cmd.name}`}>
              <b>{cmd.name}</b> {cmd.description}
            </InfoLine>
          );
        });
        result.push(<OutputLine key="help-info-space">&nbsp;</OutputLine>);
      }
      
      // Navigasyon komutları
      if (navigationCommands.length > 0) {
        result.push(<OutputLine key="help-navigation-title">{t('help.navigationCommands')}</OutputLine>);
        navigationCommands.forEach(cmd => {
          result.push(
            <InfoLine key={`help-${cmd.name}`}>
              <b>{cmd.name}</b> {cmd.description}
            </InfoLine>
          );
        });
        result.push(<OutputLine key="help-navigation-space">&nbsp;</OutputLine>);
      }
      
      // Sosyal medya komutları
      if (socialCommands.length > 0) {
        result.push(<OutputLine key="help-social-title">{t('help.socialCommands')}</OutputLine>);
        socialCommands.forEach(cmd => {
          result.push(
            <InfoLine key={`help-${cmd.name}`}>
              <b>{cmd.name}</b> {cmd.description}
            </InfoLine>
          );
        });
        result.push(<OutputLine key="help-social-space">&nbsp;</OutputLine>);
      }
      
      // Diğer komutlar
      if (miscCommands.length > 0) {
        result.push(<OutputLine key="help-misc-title">{t('help.miscCommands')}</OutputLine>);
        miscCommands.forEach(cmd => {
          result.push(
            <InfoLine key={`help-${cmd.name}`}>
              <b>{cmd.name}</b> {cmd.description}
            </InfoLine>
          );
        });
      }
      
      return result;
    },
    
    clearTerminal: () => {
      setOutput([]); // Direkt çıktıyı temizle
      return []; // Boş dizi döndür
    },
    
    showAbout: () => {
      return [
        <OutputLine key="about-title">{t('about.title')}</OutputLine>,
        <OutputLine key="about-content">{t('about.content')}</OutputLine>
      ];
    },
    
    showSkills: () => {
      return [
        <OutputLine key="skills-title">{t('skills.title')}</OutputLine>,
        <DividerLine key="skills-divider">{getFullWidthDivider()}</DividerLine>,
        <InfoLine key="skills-languages">{t('skills.languages')}</InfoLine>,
        <InfoLine key="skills-backend">{t('skills.backend')}</InfoLine>,
        <InfoLine key="skills-frontend">{t('skills.frontend')}</InfoLine>,
        <InfoLine key="skills-databases">{t('skills.databases')}</InfoLine>,
        <InfoLine key="skills-tools">{t('skills.tools')}</InfoLine>
      ];
    },
    
    showProjects: () => {
      const projectKeys = [1, 2, 3, 4, 5, 6, 7, 8];
      const result: JSX.Element[] = [
        <OutputLine key="projects-title">{t('projects.title')}</OutputLine>,
        <DividerLine key="projects-divider">{getFullWidthDivider()}</DividerLine>,
      ];
      
      projectKeys.forEach((num, index) => {
        const projectKey = `project${num}`;
        result.push(<InfoLine key={`project-${num}-title`}>{t(`projects.${projectKey}.title`)}</InfoLine>);
        result.push(<OutputLine key={`project-${num}-desc`}>{t(`projects.${projectKey}.description`)}</OutputLine>);
        result.push(<OutputLine key={`project-${num}-tech`}>{t(`projects.${projectKey}.technologies`)}</OutputLine>);
        
        // Son projeden sonra divider ekleme
        if (index < projectKeys.length - 1) {
          result.push(<DividerLine key={`project-divider-${num}`}>{getFullWidthDivider()}</DividerLine>);
        }
      });
      
      return result;
    },
    
    showExperience: () => {
      const jobKeys = [1, 2, 3];
      const result: JSX.Element[] = [
        <OutputLine key="experience-title">{t('experience.title')}</OutputLine>,
        <DividerLine key="experience-divider">{getFullWidthDivider()}</DividerLine>,
      ];
      
      jobKeys.forEach((num, index) => {
        const jobKey = `job${num}`;
        result.push(<InfoLine key={`job-${num}-title`}>{t(`experience.${jobKey}.title`)}</InfoLine>);
        result.push(<OutputLine key={`job-${num}-company`}>{t(`experience.${jobKey}.company`)}</OutputLine>);
        result.push(<OutputLine key={`job-${num}-period`}>{t(`experience.${jobKey}.period`)}</OutputLine>);
        result.push(<OutputLine key={`job-${num}-desc`}>{t(`experience.${jobKey}.description`)}</OutputLine>);
        
        // Son işten sonra divider ekleme
        if (index < jobKeys.length - 1) {
          result.push(<DividerLine key={`job-divider-${num}`}>{getFullWidthDivider()}</DividerLine>);
        }
      });
      
      return result;
    },
    
    showEducation: () => {
      const result: JSX.Element[] = [
        <OutputLine key="education-title">{t('education.title')}</OutputLine>,
        <DividerLine key="education-divider">{getFullWidthDivider()}</DividerLine>,
      ];
      
      // i18n.ts'deki education.content alanını parçalara ayıralım
      // Format: "School - University (Year-Year)\nDescription.\n\nNext education"
      const educationContent = t('ClassicPage.education.content');
      const educationEntries = educationContent.split('\n\n');
      
      educationEntries.forEach((entry, index) => {
        const [titleLine, descriptionLine] = entry.split('\n');
        
        // Başlık ve tarih ayrımı: "School - University (Year-Year)"
        const titleMatch = titleLine.match(/(.*?) - (.*?) \((.*?)\)/);
        
        if (titleMatch) {
          const [, degree, school, period] = titleMatch;
          
          result.push(<InfoLine key={`edu-${index}-school`}>{school}</InfoLine>);
          result.push(<OutputLine key={`edu-${index}-degree`}>{degree}</OutputLine>);
          result.push(<OutputLine key={`edu-${index}-period`}>{period}</OutputLine>);
          
          if (descriptionLine) {
            result.push(<OutputLine key={`edu-${index}-desc`}>{descriptionLine}</OutputLine>);
          }
          
          // Son eğitimden sonra divider ekleme
          if (index < educationEntries.length - 1) {
            result.push(<DividerLine key={`education-divider-${index}`}>{getFullWidthDivider()}</DividerLine>);
          }
        }
      });
      
      return result;
    },
    
    showContact: () => {
      return [
        <OutputLine key="contact-title">{t('contact.title')}</OutputLine>,
        <DividerLine key="contact-divider">{getFullWidthDivider()}</DividerLine>,
        <InfoLine key="contact-email">{t('contact.email')}</InfoLine>,
        <InfoLine key="contact-phone">{t('contact.phone')}</InfoLine>,
        <InfoLine key="contact-linkedin">LinkedIn: <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openLinkedin()}>linkedin.com/in/anılorhan</span></InfoLine>,
        <InfoLine key="contact-github">GitHub: <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openGithub()}>github.com/Anil-Orhan</span></InfoLine>,
        <InfoLine key="contact-twitter">Twitter: <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openTwitter()}>twitter.com/anilorhandev</span></InfoLine>
      ];
    },
    
    downloadCV: (params: string[]) => {
      // Parametre olarak geçirilen dil, 'tr' ise Türkçe, değilse İngilizce
      const language = params[0] === 'tr' ? 'tr' : 'en';
      
      // Dil değerini doğrudan geçir
      downloadCV(language);
      
      return [
        <SuccessLine key="cv-download">
          {language === 'tr' 
            ? 'Türkçe CV indiriliyor...' 
            : 'Downloading English CV...'}
        </SuccessLine>
      ];
    },
    
    changeTheme: (params: string[]) => {
      const themeName = params[0];
      if (['cmd', 'powershell', 'ubuntu'].includes(themeName)) {
        applyTheme(themeName as 'cmd' | 'powershell' | 'ubuntu');
        // Zustand store'daki tema değerini de güncelle
        useThemeStore.getState().setTheme(themeName as 'cmd' | 'powershell' | 'ubuntu');
        return [
          <SuccessLine key="theme-applied">
            {`${t('theme.' + themeName)} ${themeName === 'cmd' ? 'teması' : 'theme'} ${i18n.language === 'tr' ? 'uygulandı' : 'applied'}`}
          </SuccessLine>
        ];
      }
      
      return [
        <ErrorLine key="theme-error">
          {i18n.language === 'tr' 
            ? 'Geçersiz tema adı. Kullanılabilir temalar: cmd, powershell, ubuntu' 
            : 'Invalid theme name. Available themes: cmd, powershell, ubuntu'}
        </ErrorLine>
      ];
    },
    
    changeLanguage: (params: string[]) => {
      const lang = params[0];
      if (['tr', 'en'].includes(lang)) {
        // Önce i18n'i güncelle, sonra özel olayı tetikle
        i18n.changeLanguage(lang);
        changeLanguage(lang as 'tr' | 'en');
        
        const message = lang === 'tr' ? 'Dil Türkçe olarak değiştirildi' : 'Language changed to English';
        return [<SuccessLine key="lang-changed">{message}</SuccessLine>];
      }
      
      return [
        <ErrorLine key="lang-error">
          {i18n.language === 'tr' 
            ? 'Geçersiz dil kodu. Kullanılabilir diller: tr, en' 
            : 'Invalid language code. Available languages: tr, en'}
        </ErrorLine>
      ];
    },
    
    echo: (params: string[]) => {
      const text = params.join(' ');
      return [<OutputLine key="echo-output">{text}</OutputLine>];
    },
    
    showDate: () => {
      const now = new Date();
      return [<OutputLine key="date-output">{now.toLocaleString()}</OutputLine>];
    },
    
    openGithub: () => {
      openGithub();
      return [<SuccessLine key="github-opened">GitHub profile opened in a new tab</SuccessLine>];
    },
    
    openLinkedin: () => {
      openLinkedin();
      return [<SuccessLine key="linkedin-opened">LinkedIn profile opened in a new tab</SuccessLine>];
    },
    
    openTwitter: () => {
      openTwitter();
      return [
        <SuccessLine key="twitter-success">{t('commands.twitter.description')}</SuccessLine>
      ];
    },
    
    switchToClassic: () => {
      setTimeout(() => {
        onSwitchToClassic();
      }, 500);
      
      return [
        <SuccessLine key="classic-success">{t('navigation.switchingToClassic')}</SuccessLine>
      ];
    },
    
    services: () => {
      // Hizmetler sayfasına geçiş yap
      setTimeout(() => {
        onSwitchToServices();
      }, 500);
      
      return [
        <SuccessLine key="services-success">{t('commands.services.description')}</SuccessLine>
      ];
    },
    
    showThemes: () => {
      return [
        <OutputLine key="themes-title">{t('themes.title')}</OutputLine>,
        <InfoLine key="theme-ubuntu">1. Ubuntu Terminal: <span style={{ color: '#E95420', marginLeft: '5px' }}>Ubuntu Terminal</span></InfoLine>,
        <InfoLine key="theme-powershell">2. PowerShell: <span style={{ color: '#1E90FF', marginLeft: '5px' }}>PowerShell</span></InfoLine>,
        <InfoLine key="theme-cmd">3. Windows CMD: <span style={{ color: '#AAAAAA', marginLeft: '5px' }}>Windows CMD</span></InfoLine>,
        <OutputLine key="theme-command">{t('themes.command')}</OutputLine>
      ];
    },
    
    // Ana sayfaya geçiş için fonksiyon
    switchToHomePage: () => {
      setTimeout(() => {
        // Ana sayfaya geçiş fonksiyonunu çağır
        onSwitchToHome();
      }, 500);
      
      return [
        <SuccessLine key="homepage-success">{t('navigation.switchingToHomePage')}</SuccessLine>
      ];
    }
  }), [t, i18n.language, commands, theme, onSwitchToClassic, onSwitchToServices, onSwitchToHome]);
  
  // Komutları yükle
  useEffect(() => {
    // Eğer terminal zaten başlatılmışsa (dışarıdan gelen prop true ise) komutları tekrar yükleme
    if (externalInitialized) {
      // Sadece input'a odaklan
      if (inputRef.current) {
        inputRef.current.focus();
      }
      return;
    }
    
    // Terminal ilk kez başlatılıyorsa
    if (!initializedRef.current) {
      setCommands(defaultCommands);
      
      // Karşılama mesajını IP adresi alındıktan sonra gösterilecek, burada göstermeye gerek yok
      // updateWelcomeMessage();
      // initializedRef.current = true;
    }
    
    // Input'a odaklan
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [setCommands, t, theme, externalInitialized]);
  
  // Karşılama mesajını güncelleyen fonksiyon
  const updateWelcomeMessage = () => {
    const welcomeElements: JSX.Element[] = [
      <OutputLine key="username">
        <span style={{ 
          color: theme === 'ubuntu' ? '#4CBB17' : 
                theme === 'powershell' ? '#FFFF00' : 
                undefined,
          fontWeight: 'bold'
        }}>
          {theme === 'ubuntu' ? `$${ipAddress}` : ipAddress}
        </span>
      </OutputLine>,
      <OutputLine key="welcome">{t('WelcomeMessage')}</OutputLine>,
      <DividerLine key="divider">{getFullWidthDivider()}</DividerLine>,
      
      // CLI'yi bilmeyen kullanıcılar için yönlendirme ekliyoruz
      <InfoLine key="classic-info">{t('ClassicPageInfo')}</InfoLine>,
      <OutputLine key="classic-link">
        <span 
          style={{ 
            color: theme === 'ubuntu' ? '#E95420' : 
                   theme === 'powershell' ? '#FFFF00' : '#0078D4',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={onSwitchToClassic}
        >
          {t('SwitchToClassicPage')}
        </span>
      </OutputLine>,
      <DividerLine key="divider2">{getFullWidthDivider()}</DividerLine>
    ];
    
    setOutput(welcomeElements);
  };
  
  // Dil değiştirme olayını dinle
  useEffect(() => {
    const handleLanguageChange = () => {
      // Mevcut çıktıları güncelle
      const currentOutputs = [...outputRef.current];
      const updatedOutputs: JSX.Element[] = [];

      // Tüm çıktı elemanlarını dil değişimiyle birlikte güncelle
      for (let i = 0; i < currentOutputs.length; i++) {
        const element = currentOutputs[i];
        const key = element.key?.toString() || "";
        
        // Komut satırlarını olduğu gibi koru
        if (key.startsWith("cmd-")) {
          updatedOutputs.push(element);
          continue;
        }
        
        // Karşılama mesajını güncelle
        if (key === "welcome") {
          updatedOutputs.push(<OutputLine key="welcome">{t('WelcomeMessage')}</OutputLine>);
          continue;
        }
        
        // Ayraç satırlarını güncelle
        if (key === "divider" || key.includes("divider")) {
          updatedOutputs.push(
            <DividerLine key={key}>
              {getFullWidthDivider()}
            </DividerLine>
          );
          continue;
        }
        
        // Hakkında bilgilerini güncelle
        if (key === "about-title") {
          updatedOutputs.push(<OutputLine key="about-title">{t('about.title')}</OutputLine>);
          continue;
        }
        if (key === "about-content") {
          updatedOutputs.push(<OutputLine key="about-content">{t('about.content')}</OutputLine>);
          continue;
        }
        
        // Yetenekler bilgilerini güncelle
        if (key === "skills-title") {
          updatedOutputs.push(<OutputLine key="skills-title">{t('skills.title')}</OutputLine>);
          continue;
        }
        if (key === "skills-languages") {
          updatedOutputs.push(<InfoLine key="skills-languages">{t('skills.languages')}</InfoLine>);
          continue;
        }
        if (key === "skills-backend") {
          updatedOutputs.push(<InfoLine key="skills-backend">{t('skills.backend')}</InfoLine>);
          continue;
        }
        if (key === "skills-frontend") {
          updatedOutputs.push(<InfoLine key="skills-frontend">{t('skills.frontend')}</InfoLine>);
          continue;
        }
        if (key === "skills-databases") {
          updatedOutputs.push(<InfoLine key="skills-databases">{t('skills.databases')}</InfoLine>);
          continue;
        }
        if (key === "skills-tools") {
          updatedOutputs.push(<InfoLine key="skills-tools">{t('skills.tools')}</InfoLine>);
          continue;
        }
        
        // Proje bilgilerini güncelle
        if (key.includes("project-") && key.includes("title")) {
          const projectNum = key.match(/\d+/)?.[0];
          if (projectNum) {
            updatedOutputs.push(<InfoLine key={key}>{t(`projects.project${projectNum}.title`)}</InfoLine>);
            continue;
          }
        }
        if (key.includes("project-") && key.includes("desc")) {
          const projectNum = key.match(/\d+/)?.[0];
          if (projectNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`projects.project${projectNum}.description`)}</OutputLine>);
            continue;
          }
        }
        if (key.includes("project-") && key.includes("tech")) {
          const projectNum = key.match(/\d+/)?.[0];
          if (projectNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`projects.project${projectNum}.technologies`)}</OutputLine>);
            continue;
          }
        }
        
        // Deneyim bilgilerini güncelle
        if (key === "experience-title") {
          updatedOutputs.push(<OutputLine key="experience-title">{t('experience.title')}</OutputLine>);
          continue;
        }
        if (key.includes("job-") && key.includes("title")) {
          const jobNum = key.match(/\d+/)?.[0];
          if (jobNum) {
            updatedOutputs.push(<InfoLine key={key}>{t(`experience.job${jobNum}.title`)}</InfoLine>);
            continue;
          }
        }
        if (key.includes("job-") && key.includes("company")) {
          const jobNum = key.match(/\d+/)?.[0];
          if (jobNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`experience.job${jobNum}.company`)}</OutputLine>);
            continue;
          }
        }
        if (key.includes("job-") && key.includes("period")) {
          const jobNum = key.match(/\d+/)?.[0];
          if (jobNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`experience.job${jobNum}.period`)}</OutputLine>);
            continue;
          }
        }
        if (key.includes("job-") && key.includes("desc")) {
          const jobNum = key.match(/\d+/)?.[0];
          if (jobNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`experience.job${jobNum}.description`)}</OutputLine>);
            continue;
          }
        }
        
        // Eğitim bilgilerini güncelle
        if (key === "education-title") {
          updatedOutputs.push(<OutputLine key="education-title">{t('education.title')}</OutputLine>);
          continue;
        }
        if (key.includes("edu-") && key.includes("school")) {
          const eduNum = key.match(/\d+/)?.[0];
          if (eduNum) {
            updatedOutputs.push(<InfoLine key={key}>{t(`education.edu${eduNum}.school`)}</InfoLine>);
            continue;
          }
        }
        if (key.includes("edu-") && key.includes("degree")) {
          const eduNum = key.match(/\d+/)?.[0];
          if (eduNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`education.edu${eduNum}.degree`)}</OutputLine>);
            continue;
          }
        }
        if (key.includes("edu-") && key.includes("period")) {
          const eduNum = key.match(/\d+/)?.[0];
          if (eduNum) {
            updatedOutputs.push(<OutputLine key={key}>{t(`education.edu${eduNum}.period`)}</OutputLine>);
            continue;
          }
        }
        
        // İletişim bilgilerini güncelle
        if (key === "contact-title") {
          updatedOutputs.push(<OutputLine key="contact-title">{t('contact.title')}</OutputLine>);
          continue;
        }
        if (key === "contact-email") {
          updatedOutputs.push(<InfoLine key="contact-email">{t('contact.email')}</InfoLine>);
          continue;
        }
        if (key === "contact-phone") {
          updatedOutputs.push(<InfoLine key="contact-phone">{t('contact.phone')}</InfoLine>);
          continue;
        }
        
        // Kategori başlıklarını güncelle
        if (key.startsWith("cat-")) {
          const category = key.substring(4);
          updatedOutputs.push(<OutputLine key={key}>{category.toUpperCase()}:</OutputLine>);
          continue;
        }
        
        // Yardım komutundaki komut açıklamalarını güncelle
        if (key.startsWith("cmd-") && key.length > 4) {
          const cmdName = key.substring(4);
          const cmd = commands.find(c => c.name === cmdName);
          if (cmd) {
            updatedOutputs.push(
              <InfoLine key={key}>
                <span style={{ display: 'inline-block', width: '120px' }}>{cmd.name}</span>
                {t(cmd.descriptionKey)}
              </InfoLine>
            );
            continue;
          }
        }
        
        // Diğer tüm içerikleri olduğu gibi koru
        updatedOutputs.push(element);
      }
      
      // Güncellenmiş çıktıları ayarla
      setOutput(updatedOutputs);
    };
    
    document.addEventListener('terminal-language-change', handleLanguageChange);
    
    return () => {
      document.removeEventListener('terminal-language-change', handleLanguageChange);
    };
  }, [t, commands]);
  
  // Terminal çıktısı güncellendiğinde en alta kaydır
  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTo({
        top: terminalBodyRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [output]);
  
  // Komut çalıştırma
  const executeCommand = (command: string) => {
    if (!command.trim()) return;
    
    // Komut çıktısına komut satırını ekle - prompt rengini koruyoruz
    const newOutput = [...output, 
      <OutputLine key={`cmd-${Date.now()}`}>
        <span style={{ 
          color: theme === 'ubuntu' ? '#4CBB17' : 
                 theme === 'powershell' ? '#FFFF00' : 
                 undefined,
          fontWeight: 'bold'
        }}>
          {theme === 'ubuntu' ? `$${ipAddress}` : ipAddress}
        </span>
        {':' + command}
      </OutputLine>
    ];
    
    // Komutu işle
    const { command: cmd, params } = processCommand(command, commands, i18n.language);
    
    if (cmd) {
      // Komut geçmişine ekle
      addToHistory({
        command,
        output: [command],
        timestamp: Date.now()
      });
      
      // Özel durumlar için kontrol
      if (cmd.functionName === 'clearTerminal') {
        setOutput([]);
        setCurrentInput('');
        return;
      }
      
      // Komut işlevini çağır
      const functionName = cmd.functionName as keyof typeof commandFunctions;
      if (commandFunctions[functionName]) {
        const result = commandFunctions[functionName](params);
        // Sonuç her zaman bir dizi olmalı
        if (Array.isArray(result)) {
          newOutput.push(...result);
        } else {
          console.error('Command function did not return an array:', functionName);
          // Hata durumunda güvenli bir şekilde devam et
          newOutput.push(
            <ErrorLine key={`error-${Date.now()}`}>
              Internal error processing command result
            </ErrorLine>
          );
        }
      } else {
        newOutput.push(
          <ErrorLine key={`error-${Date.now()}`}>
            {`Internal error: Command function '${functionName}' not implemented`}
          </ErrorLine>
        );
      }
    } else {
      // Komut bulunamadı
      newOutput.push(
        <ErrorLine key={`error-${Date.now()}`}>
          {t('UnknownCommand')} {command}
        </ErrorLine>
      );
    }
    
    setOutput(newOutput);
    setCurrentInput('');
  };
  
  // Klavye olayları
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      executeCommand(currentInput);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHistoryUp();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveHistoryDown();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      
      // Tab tuşu geldiğinde otomatik tamamlama yapması için özel işlemler
      if (!currentInput.trim()) {
        const allCommandNames = commands
          .filter(cmd => cmd.isVisible)
          .map(cmd => cmd.name);
        
        // Tüm komutları listele
        const newOutput = [
          ...output,
          <OutputLine key={`autocomplete-all-${Date.now()}`}>{t('AvailableCommands')}</OutputLine>,
          ...allCommandNames.map((cmd, index) => (
            <InfoLine key={`autocomplete-all-option-${index}`}>  {cmd}</InfoLine>
          ))
        ];
        setOutput(newOutput);
        return;
      }
      
      // Otomatik tamamlama
      const matches = autocompleteCommand(currentInput, commands);
      
      if (matches.length === 1) {
        // Tek eşleşme varsa, girişi güncelle
        setCurrentInput(matches[0]);
      } else if (matches.length > 1) {
        // Ortak önek varsa, o kadarını otomatik tamamla
        let commonPrefix = currentInput;
        let index = currentInput.length;
        
        while (index < matches[0].length) {
          const nextChar = matches[0][index];
          if (matches.every(match => match.length > index && match[index] === nextChar)) {
            commonPrefix += nextChar;
            index++;
          } else {
            break;
          }
        }
        
        if (commonPrefix.length > currentInput.length) {
          setCurrentInput(commonPrefix);
        }
        
        // Eşleşen komutları göster
        const newOutput = [
          ...output,
          <OutputLine key={`autocomplete-${Date.now()}`}>{t('AutocompleteOptions')}</OutputLine>,
          ...matches.map((match, index) => (
            <InfoLine key={`autocomplete-option-${index}`}>  {match}</InfoLine>
          ))
        ];
        setOutput(newOutput);
      }
    } else if (e.key === 'l' && e.ctrlKey) {
      // CTRL+L ile ekranı temizle (Unix benzeri)
      e.preventDefault();
      setOutput([]);
    }
  };
  
  const formatSessionTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  const themeLabel = theme === 'cmd' ? 'CMD' : theme === 'powershell' ? 'PowerShell' : 'Ubuntu';
  const langLabel = i18n.language === 'tr' ? 'TR' : 'EN';

  return (
    <TerminalContainer $theme={theme}>
      <TerminalHeader>
        <WindowControls>
          <WindowDot $color="#FF5F56" />
          <WindowDot $color="#FFBD2E" />
          <WindowDot $color="#27C93F" />
        </WindowControls>
        <HeaderTitle>{t('TerminalHeader')}</HeaderTitle>
        <HeaderActions>
          <HeaderActionIcon>&#x2500;</HeaderActionIcon>
          <HeaderActionIcon>&#x25A1;</HeaderActionIcon>
          <HeaderActionIcon>&#x2715;</HeaderActionIcon>
        </HeaderActions>
      </TerminalHeader>
      <TerminalBody ref={terminalBodyRef}>
        {output}
      </TerminalBody>
      <TerminalInput>
        <PromptSpan style={{
          color: theme === 'ubuntu' ? '#4CBB17' :
                 theme === 'powershell' ? '#FFFF00' :
                 undefined
        }}>
          {theme === 'ubuntu' ? `$${ipAddress}` : ipAddress}
        </PromptSpan>
        <InputField
          ref={inputRef}
          type="text"
          value={currentInput}
          onChange={(e) => setCurrentInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('CommandInputPlaceholder')}
          autoFocus
          autoComplete="off"
        />
      </TerminalInput>
      <StatusBar>
        <StatusItem>{themeLabel}</StatusItem>
        <StatusDivider />
        <StatusItem>{langLabel}</StatusItem>
        <StatusDivider />
        <StatusItem>{ipAddress || '...'}</StatusItem>
        <StatusDivider />
        <StatusItem>Session: {formatSessionTime(sessionSeconds)}</StatusItem>
        <StatusDivider />
        <StatusItem>History: {history.length} cmds</StatusItem>
      </StatusBar>
    </TerminalContainer>
  );
};

export default Terminal; 